import React, { Component } from 'react';
import {
  Row,
  Col,
  Table,
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  Input,
  InputGroup,
  InputGroupAddon,
  Button,
  Card,
  CardBody,
} from 'reactstrap';
import axios from 'axios';
import { toast } from 'react-toastify';

import Widget from '../../../components/Widget';
import config from '../../../config';
import { useDateFilter } from '../../../contexts/DateFilterContext';
import { useCity } from '../../../contexts/CityContext';

// Create a dedicated axios instance for dispatch API to avoid conflicts
const dispatchApi = axios.create({
  baseURL: config.dispatchApiBase,
});

// Add auth interceptor to the instance
dispatchApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Pagination styles
const paginationStyles = {
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px',
    gap: '20px',
    borderTop: '1px solid #e5e7eb'
  },
  pageBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    border: '1px solid #d1d5db',
    background: 'white',
    borderRadius: '8px',
    color: '#374151',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  pageBtnDisabled: {
    opacity: '0.5',
    cursor: 'not-allowed',
    background: '#f3f4f6'
  },
  pageInfo: {
    color: '#6b7280',
    fontSize: '14px',
    fontWeight: '500'
  }
};

class ManualAssignments extends Component {
  constructor(props) {
    super(props);
    
    this.state = {
      searchTerm: '',
      assignments: [],
      isFetching: false,
      error: null,
      // Pagination state
      currentPage: 1,
      itemsPerPage: 20,
      // Auto-refresh countdown
      refreshIntervalSeconds: 30,
      nextRefreshIn: 30,
    };
    
    // Countdown timer for auto-refresh
    this.countdownTimer = null;
  }

  componentDidMount() {
    this.fetchAssignments();
    // Auto-refresh every 30 seconds
    this.refreshInterval = setInterval(this.fetchAssignments, 30000);
    // Start countdown timer
    this.countdownTimer = setInterval(() => {
      this.setState(prev => ({
        nextRefreshIn: prev.nextRefreshIn > 0 ? prev.nextRefreshIn - 1 : 0
      }));
    }, 1000);
  }

  componentDidUpdate(prevProps) {
    // Refetch when selected cities change
    if (JSON.stringify(prevProps.selectedCities) !== JSON.stringify(this.props.selectedCities)) {
      this.fetchAssignments();
    }
  }

  componentWillUnmount() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
  }

  fetchAssignments = async () => {
    this.setState({ isFetching: true, error: null });
    try {
      const { selectedCities } = this.props;
      
      // Build cities parameter
      const citiesParam = selectedCities && selectedCities.length > 0 
        ? `?cities=${selectedCities.join(',')}` 
        : '';
      
      const response = await dispatchApi.get(`/manual-assignments/history${citiesParam}`);
      this.setState({ 
        assignments: response.data.assignments || [],
        isFetching: false,
        nextRefreshIn: this.state.refreshIntervalSeconds,
      });
    } catch (error) {
      console.error('Failed to fetch manual assignments:', error);
      this.setState({ 
        error: error.response?.data?.detail || 'Failed to fetch manual assignments',
        isFetching: false 
      });
      toast.error('Failed to fetch manual assignments');
    }
  }

  handleSearch = (e) => {
    this.setState({ searchTerm: e.target.value });
  }

  handlePageChange = (page) => {
    this.setState({ currentPage: page });
  }

  filterAssignments = (assignments) => {
    const searchLower = this.state.searchTerm.toLowerCase();
    return assignments.filter(assignment => {
      const matchesSearch = 
        String(assignment.booking_id).includes(searchLower) ||
        String(assignment.vehicle_id).includes(searchLower) ||
        (assignment.assigned_by || '').toLowerCase().includes(searchLower) ||
        (assignment.vehicle?.registration_number || '').toLowerCase().includes(searchLower) ||
        (assignment.vehicle?.driver_name || '').toLowerCase().includes(searchLower) ||
        (assignment.booking?.pickup_location || '').toLowerCase().includes(searchLower);
      
      if (!matchesSearch) return false;
      
      // Apply date filter if available
      if (this.props.isDateInRange) {
        const assignmentDate = assignment.assigned_at || assignment.created_at || assignment.timestamp;
        if (!assignmentDate) return true; // No date field, include by default
        return this.props.isDateInRange(assignmentDate);
      }
      
      return true;
    });
  }

  formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getSourceBadge = (source) => {
    const sourceMap = {
      'unlocked_bookings': { color: 'warning', text: 'Unlocked Bookings' },
      'trips_page': { color: 'info', text: 'Trips Page' },
      'unassigned_page': { color: 'danger', text: 'Unassigned Page' },
      'vehicle_page': { color: 'success', text: 'Vehicle Page' },
      'dashboard': { color: 'primary', text: 'Dashboard' },
    };
    const source_data = sourceMap[source] || { color: 'secondary', text: source };
    return <Badge color={source_data.color}>{source_data.text}</Badge>;
  }

  getStatusBadge = (status) => {
    const statusMap = {
      'completed': 'success',
      'pending': 'warning',
      'failed': 'danger',
    };
    return <Badge color={statusMap[status] || 'secondary'}>{status || 'Unknown'}</Badge>;
  }

  render() {
    const { searchTerm, assignments, isFetching, error, currentPage, itemsPerPage } = this.state;
    const filteredAssignments = this.filterAssignments(assignments);
    const totalPages = Math.ceil(filteredAssignments.length / itemsPerPage);
    const paginatedAssignments = filteredAssignments.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );

    if (error) {
      return (
        <div>
          <Breadcrumb>
            <BreadcrumbItem>YOU ARE HERE</BreadcrumbItem>
            <BreadcrumbItem active>Manual Assignments</BreadcrumbItem>
          </Breadcrumb>
          
          <Row>
            <Col sm={12}>
              <Widget title="Manual Assignment History">
                <div className="alert alert-danger">
                  Error loading assignments: {error}
                  <br />
                  <Button color="primary" onClick={this.fetchAssignments}>
                    Retry
                  </Button>
                </div>
              </Widget>
            </Col>
          </Row>
        </div>
      );
    }

    return (
      <div>
        <Breadcrumb>
          <BreadcrumbItem>YOU ARE HERE</BreadcrumbItem>
          <BreadcrumbItem active>Manual Assignments</BreadcrumbItem>
        </Breadcrumb>
        
        <Row>
          <Col sm={12}>
            <Widget
              title={
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mt-0 mb-0">
                    <i className="fa fa-hand-paper-o mr-xs opacity-70" />{' '}
                    Manual Assignment History
                  </h5>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={this.fetchAssignments}
                    disabled={isFetching}
                    title={`Auto-refreshing in ${this.state.nextRefreshIn}s`}
                  >
                    <i className={`fa ${isFetching ? 'fa-spinner fa-spin' : 'fa-refresh'} mr-1`} />
                    Refresh {!isFetching && `(${this.state.nextRefreshIn}s)`}
                  </Button>
                </div>
              }
            >
              <Row className="mb-3">
                <Col sm={6}>
                  <InputGroup>
                    <Input 
                      placeholder="Search by booking ID, vehicle, driver, or operator..." 
                      value={searchTerm}
                      onChange={this.handleSearch}
                    />
                    <InputGroupAddon addonType="append">
                      <Button color="default">
                        <i className="fa fa-search" />
                      </Button>
                    </InputGroupAddon>
                  </InputGroup>
                </Col>
                <Col sm={6} className="text-right">
                  <div className="text-muted">
                    <small>
                      Total Assignments: <strong>{assignments.length}</strong> | 
                      Showing: <strong>{Math.min((currentPage - 1) * itemsPerPage + 1, filteredAssignments.length)}-{Math.min(currentPage * itemsPerPage, filteredAssignments.length)}</strong> of <strong>{filteredAssignments.length}</strong>
                    </small>
                  </div>
                </Col>
              </Row>

              {filteredAssignments.length === 0 && !isFetching ? (
                <Card>
                  <CardBody className="text-center py-5">
                    <i className="fa fa-inbox fa-3x text-muted mb-3" />
                    <h5 className="text-muted">No Manual Assignments Found</h5>
                    <p className="text-muted">
                      {searchTerm 
                        ? 'Try adjusting your search criteria' 
                        : 'Manual assignments will appear here when operators assign bookings'}
                    </p>
                  </CardBody>
                </Card>
              ) : (
                <div className="table-responsive">
                  <Table hover striped>
                    <thead>
                      <tr>
                        <th>Booking ID</th>
                        <th>Vehicle</th>
                        <th>Driver</th>
                        <th>Pickup Time</th>
                        <th>Pickup Location</th>
                        <th>Drop Location</th>
                        <th>Distance</th>
                        <th>Assigned By</th>
                        <th>Source</th>
                        <th>Assigned At</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isFetching && filteredAssignments.length === 0 ? (
                        <tr>
                          <td colSpan="11" className="text-center py-4">
                            <i className="fa fa-spinner fa-spin mr-2" />
                            Loading manual assignments...
                          </td>
                        </tr>
                      ) : (
                        paginatedAssignments.map((assignment, index) => (
                          <tr key={`${assignment.booking_id}-${assignment.timestamp}-${index}`}>
                            <td>
                              <strong>{assignment.booking_id}</strong>
                            </td>
                            <td>
                              <div>
                                <strong>{assignment.vehicle_id}</strong>
                                {assignment.vehicle && (
                                  <div>
                                    <small className="text-muted">
                                      {assignment.vehicle.registration_number}
                                    </small>
                                    <br />
                                    <Badge color="secondary" className="mt-1">
                                      {assignment.vehicle.vehicle_class}
                                    </Badge>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              {assignment.vehicle?.driver_name ? (
                                <div>
                                  {assignment.vehicle.driver_name}
                                  <br />
                                  <small className="text-muted">
                                    {assignment.vehicle.driver_phone}
                                  </small>
                                </div>
                              ) : (
                                <span className="text-muted">N/A</span>
                              )}
                            </td>
                            <td>
                              <small>
                                {assignment.booking?.pickup_time 
                                  ? this.formatDate(assignment.booking.pickup_time)
                                  : 'N/A'}
                              </small>
                            </td>
                            <td>
                              <small>
                                {assignment.booking?.pickup_location 
                                  ? assignment.booking.pickup_location.substring(0, 30) + (assignment.booking.pickup_location.length > 30 ? '...' : '')
                                  : 'N/A'}
                              </small>
                            </td>
                            <td>
                              <small>
                                {assignment.booking?.drop_location 
                                  ? assignment.booking.drop_location.substring(0, 30) + (assignment.booking.drop_location.length > 30 ? '...' : '')
                                  : 'N/A'}
                              </small>
                            </td>
                            <td>
                              <small>
                                {assignment.booking?.distance_km 
                                  ? `${assignment.booking.distance_km.toFixed(1)} km`
                                  : 'N/A'}
                              </small>
                            </td>
                            <td>
                              <strong>{assignment.assigned_by || 'Unknown'}</strong>
                            </td>
                            <td>
                              {this.getSourceBadge(assignment.source)}
                            </td>
                            <td>
                              <small>
                                {this.formatDate(assignment.processed_at || assignment.timestamp)}
                              </small>
                            </td>
                            <td>
                              {this.getStatusBadge(assignment.status)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
              
              {/* Pagination Controls */}
              {filteredAssignments.length > itemsPerPage && (
                <div style={paginationStyles.pagination}>
                  <button
                    disabled={currentPage === 1}
                    onClick={() => this.handlePageChange(currentPage - 1)}
                    style={{
                      ...paginationStyles.pageBtn,
                      ...(currentPage === 1 ? paginationStyles.pageBtnDisabled : {})
                    }}
                  >
                    <i className="fa fa-chevron-left" /> Previous
                  </button>
                  <span style={paginationStyles.pageInfo}>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => this.handlePageChange(currentPage + 1)}
                    style={{
                      ...paginationStyles.pageBtn,
                      ...(currentPage === totalPages ? paginationStyles.pageBtnDisabled : {})
                    }}
                  >
                    Next <i className="fa fa-chevron-right" />
                  </button>
                </div>
              )}
            </Widget>
          </Col>
        </Row>
      </div>
    );
  }
}

// HOC to inject date filter and city context
const ManualAssignmentsWithDateFilter = (props) => {
  const { isDateInRange } = useDateFilter();
  const { selectedCities } = useCity();
  return <ManualAssignments {...props} isDateInRange={isDateInRange} selectedCities={selectedCities} />;
};

export default ManualAssignmentsWithDateFilter;
